import type { ImageProbeReport, MarkerVerdict } from 'trace-viewer/image-utils';
import type { UiState } from './main';
import {
  axesToFixes,
  escapeHtml,
  formatFileSize,
  renderChecks,
  renderDisclosure,
  renderDropZone,
  renderFacts,
  renderFileChip,
  renderFixSection,
  renderKv,
  renderPageHeader,
  renderStatusHead,
} from '../shared/ui';

interface RenderProps {
  state: UiState;
  verdict: MarkerVerdict | null;
}

const ACCEPT = 'image/*,.jpg,.jpeg,.png,.webp,.avif,.bmp';

/** Kept in sync with MAX_VISIBLE in keypoint-overlay.ts. */
const MAX_VISIBLE_POINTS = 400;

/** Plain-language names for each check. */
const CHECK_LABELS: Record<string, string> = {
  resolution: 'Resolution',
  aspect: 'Shape',
  features: 'Amount of detail',
  distribution: 'Detail spread',
  uniqueness: 'Repeating patterns',
  contrast: 'Contrast',
  exposure: 'Exposure',
  blur: 'Sharpness',
};

function busyMessage(state: UiState): string {
  if (state.phase === 'fetching') return 'Downloading the image…';
  if (state.phase === 'loading-cv') return 'Loading the analyzer (one-time, about 10 MB)…';
  if (state.phase === 'analyzing') return 'Finding tracking points…';
  return '';
}

function renderResult(state: UiState, verdict: MarkerVerdict | null): string {
  if (!state.previewUrl) return '';
  const report = state.report;
  const busy = busyMessage(state);

  const head = verdict
    ? renderStatusHead(verdict.overall, verdict.headline, 'Scored on detail, spread, uniqueness, contrast, exposure and sharpness.')
    : busy
      ? renderStatusHead('busy', busy)
      : report?.bailout
        ? renderStatusHead('red', 'Couldn’t analyze this image', escapeHtml(report.bailout))
        : '';

  const facts: Array<[string, string]> = [];
  if (report) facts.push(['Resolution', `${report.file.width} × ${report.file.height}`]);
  if (report?.features) {
    facts.push(['Tracking points', report.features.keypointCount.toLocaleString()]);
    facts.push(['Coverage', `${Math.round(report.features.distribution * 100)}%`]);
  }

  const count = report?.features?.keypoints.length ?? 0;
  const toggle = count > 0
    ? `<label class="toggle">
         <input type="checkbox" data-action="toggle-keypoints" ${state.showKeypoints ? 'checked' : ''} />
         Show tracking points${count > MAX_VISIBLE_POINTS ? ` (strongest ${MAX_VISIBLE_POINTS})` : ''}
       </label>`
    : '';

  const notes = report?.warnings.length
    ? `<div class="notice info">${report.warnings.map(w => escapeHtml(w.message)).join(' ')}</div>`
    : '';

  return `
    <section class="result">
      <div class="result-body">
        <div class="status-line">${head}${toggle}</div>
        ${renderFacts(facts)}
        ${notes}
      </div>
      <div class="marker-stage">
        <div class="preview-stack">
          <img src="${escapeHtml(state.previewUrl)}" alt="Your marker image" class="marker-preview" />
          <canvas class="keypoint-overlay" aria-hidden="true"></canvas>
        </div>
      </div>
    </section>
  `;
}

function measurementRows(report: ImageProbeReport): Array<[string, string]> {
  const rows: Array<[string, string]> = [
    ['Format', report.file.format.toUpperCase()],
    ['Resolution', `${report.file.width} × ${report.file.height}`],
    ['File size', formatFileSize(report.file.fileSize)],
  ];
  if (report.features) {
    rows.push(['Tracking points', `${report.features.keypointCount} (${report.features.keypointsPerMegapixel.toFixed(0)} per megapixel)`]);
    rows.push(['Coverage', `${Math.round(report.features.distribution * 100)}% of an 8 × 8 grid`]);
  }
  if (report.uniqueness) {
    rows.push(['Repeating patches', `${Math.round(report.uniqueness.selfMatchRatio * 100)}% of ${report.uniqueness.descriptorsAnalyzed}`]);
  }
  if (report.quality) {
    rows.push(['Contrast (luma σ)', report.quality.contrast.toFixed(1)]);
    rows.push(['Mean brightness', report.quality.meanLuma.toFixed(0)]);
    rows.push(['Crushed shadows', `${(report.quality.clippedShadowFraction * 100).toFixed(1)}%`]);
    rows.push(['Blown highlights', `${(report.quality.clippedHighlightFraction * 100).toFixed(1)}%`]);
    rows.push(['Sharpness (Laplacian variance)', report.quality.laplacianVariance.toFixed(0)]);
  }
  if (report.analysisMs > 0) rows.push(['Analysis time', `${(report.analysisMs / 1000).toFixed(2)} s`]);
  return rows;
}

function renderDetails(report: ImageProbeReport | null, verdict: MarkerVerdict | null): string {
  if (!report || !verdict) return '';
  return `
    <section class="section">
      <h2 class="section-title">Details</h2>
      ${renderDisclosure('checks', 'All checks', renderChecks(verdict.axes, CHECK_LABELS), {
        meta: `${verdict.axes.filter(a => a.status === 'green').length} of ${verdict.axes.length} pass`,
      })}
      ${renderDisclosure('measurements', 'Measurements', renderKv(measurementRows(report)))}
    </section>
  `;
}

/** Shown before a file is chosen: what "good" looks like, in one breath. */
function renderPrimer(): string {
  return `
    <section class="section">
      <h2 class="section-title">What makes a good marker</h2>
      <p class="setting-help" style="font-size:0.9375rem;max-width:40rem;">
        Trackers lock on to sharp corners and edges. The best markers have lots of them, spread across the whole
        picture, with little repetition and good contrast — think a detailed poster or photo, not a logo on a plain
        background or a repeating pattern.
      </p>
    </section>
  `;
}

export function renderApp(root: HTMLElement, props: RenderProps): void {
  const { state, verdict } = props;

  let extraMeta = '';
  if (state.srcUrl) {
    extraMeta = `from <a href="${escapeHtml(state.srcUrl)}" target="_blank" rel="noopener" title="${escapeHtml(state.srcUrl)}">${escapeHtml(state.srcUrl)}</a>`;
  }
  const input = state.file
    ? renderFileChip(state.file, ACCEPT, extraMeta)
    : renderDropZone({ accept: ACCEPT, prompt: 'Drop an image here', hint: 'JPEG, PNG, WebP or AVIF. You can also link one with ?src=URL.' });

  const fetching = !state.file && state.phase === 'fetching'
    ? `<div class="busy-line"><span class="status-light busy"></span>Downloading the image…</div>`
    : '';
  const error = state.error
    ? `<div class="notice error" style="margin-top:1rem;"><strong>Couldn’t check this image.</strong> ${escapeHtml(state.error)}</div>`
    : '';

  const fixes = verdict
    ? renderFixSection('What to improve', axesToFixes(verdict.axes, CHECK_LABELS), 'Nothing to improve. This image should track well.')
    : '';

  root.innerHTML = `
    ${renderPageHeader('image', 'Marker check', 'See whether a picture will track well as an AR image marker, and what to change if it won’t.', 'Images stay on your device — only the analyzer is downloaded.')}
    ${input}
    ${fetching}
    ${error}
    ${renderResult(state, verdict)}
    ${fixes}
    ${renderDetails(state.report, verdict)}
    ${state.file ? '' : renderPrimer()}
  `;
}
